import { Avatar, Box, Divider, ListItem, ListItemAvatar, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ConversationListItem } from '../types/messages.types';
import { formatConversationTimestamp, truncateText } from '@/shared/utils/formatters';
import { SystemConversationItem } from './SystemConversationItem';

const MESSAGE_PREVIEW_LENGTH = 48;

interface ConversationItemProps {
  conversation: ConversationListItem;
  showDivider: boolean;
  onClick?: () => void;
}

const getInitials = (name: string) => {
  const parts = name.trim().split(' ').filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
};

export function ConversationItem({ conversation, showDivider, onClick }: ConversationItemProps) {
  const { t, i18n } = useTranslation();

  if (conversation.isSystemConversation) {
    return (
      <SystemConversationItem
        conversation={conversation}
        showDivider={showDivider}
        onClick={onClick}
      />
    );
  }

  const displayName = conversation.interlocutor.name;
  const offerTitle = conversation.offerTitle || t('messages.offerTitleFallback');
  const lastMessage = conversation.lastMessage
    ? truncateText(conversation.lastMessage, MESSAGE_PREVIEW_LENGTH)
    : '';
  const timestamp = conversation.lastMessageAt
    ? formatConversationTimestamp(
        conversation.lastMessageAt,
        i18n.language,
        t('messages.yesterday')
      )
    : '';

  return (
    <Box>
      <ListItem
        alignItems="flex-start"
        sx={{
          px: 3,
          py: 2,
          cursor: onClick ? 'pointer' : 'default',
          bgcolor: 'transparent',
          '&:hover': onClick ? { bgcolor: 'action.hover' } : {},
        }}
        onClick={onClick}
        data-testid={`conversation-item-${conversation.id}`}
      >
        <ListItemAvatar>
          <Avatar sx={{ bgcolor: 'primary.main' }}>{getInitials(displayName)}</Avatar>
        </ListItemAvatar>
        <Box sx={{ flex: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
              {displayName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {timestamp}
            </Typography>
            {conversation.hasUnread && (
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: 'secondary.main',
                }}
              />
            )}
          </Stack>
          <Stack spacing={0.5}>
            <Typography variant="body2" color="text.secondary">
              {offerTitle}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              fontWeight={conversation.hasUnread ? 600 : 400}
            >
              {lastMessage}
            </Typography>
          </Stack>
        </Box>
      </ListItem>
      {showDivider && <Divider component="li" />}
    </Box>
  );
}
